import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { api } from '../../api/apiService';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

type FastDomain = { id: string; host_pattern: string };
type FastDraftCriterion = { id: string; name?: string };
type PublishVenue = { id: string; slug: string; name: string };

export type FastRule = {
  id: string;
  domain_id: string;
  draft_criterion_id?: string;
  publish_venues?: string[];
  action: string;
  enabled: boolean;
};

export type DashboardSummary = {
  since: number;
  until: number;
  total: number;
  total_words: number;
  human_hours_saved: number;
  avg_per_day: number;
  by_domain: { id: string; label: string; count: number }[];
  by_criterion: { id: string; label: string; count: number }[];
  by_venue: { id: string; label: string; count: number }[];
  by_weekday: { key: number; label: string; count: number }[];
  by_hour_utc: { key: number; label: string; count: number }[];
};

function utcStartOfTodayUnix(): number {
  const n = new Date();
  return Math.floor(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()) / 1000);
}

function utcStartMinusCalendarDays(days: number): number {
  const n = new Date();
  const t = Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate() - days);
  return Math.floor(t / 1000);
}

const MILESTONE_DEFS = [
  { label: 'Today', sinceFn: () => utcStartOfTodayUnix() },
  { label: 'Yesterday', sinceFn: () => utcStartMinusCalendarDays(1) },
  { label: '3 days ago', sinceFn: () => utcStartMinusCalendarDays(3) },
  { label: '5 days ago', sinceFn: () => utcStartMinusCalendarDays(5) },
  { label: 'Week ago', sinceFn: () => utcStartMinusCalendarDays(7) },
  { label: 'Month ago', sinceFn: () => utcStartMinusCalendarDays(30) },
  { label: '3 months ago', sinceFn: () => utcStartMinusCalendarDays(90) },
] as const;

const fieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': { color: '#fafafa' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#52525b' },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
};

function ruleMatchesOrFilters(
  rule: FastRule,
  domainId: string,
  criterionId: string,
  venueSlug: string
): boolean {
  const hasD = Boolean(domainId);
  const hasC = Boolean(criterionId);
  const hasV = Boolean(venueSlug);
  if (!hasD && !hasC && !hasV) {
    return true;
  }
  if (hasD && rule.domain_id === domainId) {
    return true;
  }
  if (hasC && rule.draft_criterion_id === criterionId) {
    return true;
  }
  const venues = rule.publish_venues ?? [];
  if (hasV && venues.some((s) => s === venueSlug)) {
    return true;
  }
  return false;
}

function actionShort(a: string): string {
  if (a.includes('publish')) return 'Publish';
  if (a.includes('ready')) return 'Ready';
  return 'Draft';
}

function uniqueSorted(ids: string[]): string[] {
  return Array.from(new Set(ids)).sort();
}

/** API rule_ids: omit when unscoped “all profile rules” only; otherwise send explicit ids. */
function computeRuleIdsForApi(
  rules: FastRule[],
  domainId: string,
  criterionId: string,
  venueSlug: string,
  selectedRuleIds: string[]
): string[] | null {
  const hasOr = Boolean(domainId || criterionId || venueSlug);
  const visible = rules.filter((r) => ruleMatchesOrFilters(r, domainId, criterionId, venueSlug));

  if (hasOr && visible.length === 0) {
    return [];
  }
  if (selectedRuleIds.length === 0) {
    return [];
  }

  const selectedSet = new Set(selectedRuleIds);
  const selectedInVisible = visible.filter((r) => selectedSet.has(r.id)).map((r) => r.id);

  if (!hasOr && rules.length > 0 && selectedSet.size === rules.length && rules.every((r) => selectedSet.has(r.id))) {
    return null;
  }

  return uniqueSorted(selectedInVisible);
}

export type FastTrackingDashboardTabProps = {
  basePath: string;
  domains: FastDomain[];
  draftCriteria: FastDraftCriterion[];
  publishVenues: PublishVenue[];
  rules: FastRule[];
  onHttpError: (msg: string | null) => void;
};

export const FastTrackingDashboardTab: React.FC<FastTrackingDashboardTabProps> = ({
  basePath,
  domains,
  draftCriteria,
  publishVenues,
  rules,
  onHttpError,
}) => {
  const [milestoneIdx, setMilestoneIdx] = useState(2);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [domainId, setDomainId] = useState('');
  const [criterionId, setCriterionId] = useState('');
  const [venueSlug, setVenueSlug] = useState('');
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (rules.length === 0) {
      setSelectedRuleIds([]);
      return;
    }
    setSelectedRuleIds((prev) => {
      const allIds = rules.map((r) => r.id);
      if (prev.length === 0) {
        return allIds;
      }
      const valid = new Set(allIds);
      const kept = prev.filter((id) => valid.has(id));
      return kept.length > 0 ? kept : allIds;
    });
  }, [rules]);

  const domainById = useMemo(() => {
    const m = new Map<string, string>();
    domains.forEach((d) => m.set(d.id, d.host_pattern));
    return m;
  }, [domains]);

  const criterionById = useMemo(() => {
    const m = new Map<string, string>();
    draftCriteria.forEach((c) => m.set(c.id, (c.name ?? '').trim() || c.id));
    return m;
  }, [draftCriteria]);

  const visibleRules = useMemo(
    () => rules.filter((r) => ruleMatchesOrFilters(r, domainId, criterionId, venueSlug)),
    [rules, domainId, criterionId, venueSlug]
  );

  const selectedRuleIdSet = useMemo(() => new Set(selectedRuleIds), [selectedRuleIds]);
  const selectedInVisibleCount = useMemo(
    () => visibleRules.filter((r) => selectedRuleIdSet.has(r.id)).length,
    [visibleRules, selectedRuleIdSet]
  );
  const allVisibleChecked = visibleRules.length > 0 && selectedInVisibleCount === visibleRules.length;
  const someVisibleChecked = selectedInVisibleCount > 0 && !allVisibleChecked;

  const ruleIdsForApi = useMemo(
    () => computeRuleIdsForApi(rules, domainId, criterionId, venueSlug, selectedRuleIds),
    [rules, domainId, criterionId, venueSlug, selectedRuleIds]
  );

  const sinceUnix = useMemo(() => MILESTONE_DEFS[milestoneIdx]?.sinceFn() ?? utcStartMinusCalendarDays(3), [milestoneIdx]);

  const reload = useCallback(async () => {
    onHttpError(null);
    setLoading(true);
    try {
      const until = Math.floor(Date.now() / 1000);
      const q = new URLSearchParams();
      q.set('since', String(sinceUnix));
      q.set('until', String(until));

      if (ruleIdsForApi !== null && ruleIdsForApi.length === 0) {
        setSummary({
          since: sinceUnix,
          until,
          total: 0,
          total_words: 0,
          human_hours_saved: 0,
          avg_per_day: 0,
          by_domain: [],
          by_criterion: [],
          by_venue: [],
          by_weekday: [],
          by_hour_utc: [],
        });
        return;
      }
      if (ruleIdsForApi !== null && ruleIdsForApi.length > 0) {
        q.set('rule_ids', ruleIdsForApi.join(','));
      }

      const res = (await api.get(`${basePath}/dashboard?${q.toString()}`)) as DashboardSummary;
      setSummary(res);
    } catch (e: unknown) {
      setSummary(null);
      onHttpError(e instanceof Error ? e.message : 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [basePath, sinceUnix, ruleIdsForApi, onHttpError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleOrFilterChange = (patch: { domainId?: string; criterionId?: string; venueSlug?: string }) => {
    const nextD = patch.domainId !== undefined ? patch.domainId : domainId;
    const nextC = patch.criterionId !== undefined ? patch.criterionId : criterionId;
    const nextV = patch.venueSlug !== undefined ? patch.venueSlug : venueSlug;
    if (patch.domainId !== undefined) setDomainId(patch.domainId);
    if (patch.criterionId !== undefined) setCriterionId(patch.criterionId);
    if (patch.venueSlug !== undefined) setVenueSlug(patch.venueSlug);

    const nextVisible = rules.filter((r) => ruleMatchesOrFilters(r, nextD, nextC, nextV));
    const allowed = new Set(nextVisible.map((r) => r.id));
    setSelectedRuleIds((prev) => prev.filter((id) => allowed.has(id)));
  };

  const toggleRule = (id: string) => {
    setSelectedRuleIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
      }
      return uniqueSorted(Array.from(s));
    });
  };

  const handleSelectAllVisible = (checked: boolean) => {
    if (checked) {
      setSelectedRuleIds(uniqueSorted(visibleRules.map((r) => r.id)));
    } else {
      setSelectedRuleIds([]);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const hasOrFilter = Boolean(domainId || criterionId || venueSlug);
  const isDefaultScope =
    !hasOrFilter &&
    rules.length > 0 &&
    selectedRuleIds.length === rules.length &&
    rules.every((r) => selectedRuleIdSet.has(r.id));

  const filterBadgeInvisible = isDefaultScope && selectedRuleIds.length > 0;

  const sliderMarks = MILESTONE_DEFS.map((m, i) => ({ value: i, label: m.label }));

  const weekdayChart = useMemo(() => {
    if (!summary?.by_weekday?.length) return null;
    return {
      labels: summary.by_weekday.map((x) => x.label),
      datasets: [
        {
          label: 'Runs',
          data: summary.by_weekday.map((x) => x.count),
          backgroundColor: 'rgba(129, 140, 248, 0.65)',
        },
      ],
    };
  }, [summary]);

  const hourChart = useMemo(() => {
    if (!summary?.by_hour_utc?.length) return null;
    return {
      labels: summary.by_hour_utc.map((x) => String(x.key)),
      datasets: [
        {
          label: 'Runs (UTC hour)',
          data: summary.by_hour_utc.map((x) => x.count),
          backgroundColor: 'rgba(96, 165, 250, 0.65)',
        },
      ],
    };
  }, [summary]);

  const chartOpts = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { labels: { color: '#e4e4e7' } },
        title: { display: false },
      },
      scales: {
        x: {
          ticks: { color: '#a1a1aa' },
          grid: { color: 'rgba(82, 82, 91, 0.35)' },
        },
        y: {
          ticks: { color: '#a1a1aa', precision: 0 },
          grid: { color: 'rgba(82, 82, 91, 0.35)' },
        },
      },
    }),
    []
  );

  const scopeSummary = useMemo(() => {
    if (rules.length === 0) {
      return 'No rules in profile.';
    }
    if (ruleIdsForApi !== null && ruleIdsForApi.length === 0) {
      return 'No rules selected — dashboard shows zeros.';
    }
    if (ruleIdsForApi === null) {
      return 'All rules · full profile';
    }
    return `${ruleIdsForApi.length} rule${ruleIdsForApi.length === 1 ? '' : 's'}`;
  }, [rules.length, ruleIdsForApi]);

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Tooltip title="Open filters and rule selection (updates apply immediately)">
            <Badge color="primary" variant="dot" invisible={filterBadgeInvisible}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: '#fafafa',
                  borderColor: '#3f3f46',
                  '&:hover': { borderColor: '#a1a1aa', bgcolor: '#27272a' },
                }}
              >
                Rules &amp; filters
              </Button>
            </Badge>
          </Tooltip>
          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
            {scopeSummary}
            {hasOrFilter ? ` · OR filters on (${selectedInVisibleCount} in scope)` : ''}
          </Typography>
        </Stack>
      </Stack>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 420 },
            maxWidth: { xs: '100vw', sm: 480 },
            bgcolor: '#18181b',
            borderLeft: '1px solid #27272a',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #27272a',
              bgcolor: '#09090b',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 700, pr: 1 }}>
              Rules &amp; filters
            </Typography>
            <IconButton onClick={closeDrawer} aria-label="Close" sx={{ color: '#fafafa' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#e4e4e7', fontWeight: 600, mb: 0.5 }}>
              OR filters (narrow the rule list)
            </Typography>
            <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mb: 1.5 }}>
              A rule appears if it matches domain, draft criterion, or publish venue. Then choose which of those
              rules count toward the dashboard. Changes apply immediately.
            </Typography>

            <Stack spacing={1.5}>
              <FormControl size="small" sx={fieldSx}>
                <InputLabel id="dash-domain">Domain</InputLabel>
                <Select
                  labelId="dash-domain"
                  label="Domain"
                  value={domainId}
                  onChange={(e) => handleOrFilterChange({ domainId: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>
                  {domains.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.host_pattern}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={fieldSx}>
                <InputLabel id="dash-crit">Draft criterion</InputLabel>
                <Select
                  labelId="dash-crit"
                  label="Draft criterion"
                  value={criterionId}
                  onChange={(e) => handleOrFilterChange({ criterionId: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>
                  {draftCriteria.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {(c.name ?? '').trim() || c.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={fieldSx}>
                <InputLabel id="dash-venue">Publish venue</InputLabel>
                <Select
                  labelId="dash-venue"
                  label="Publish venue"
                  value={venueSlug}
                  onChange={(e) => handleOrFilterChange({ venueSlug: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>
                  {publishVenues.map((v) => (
                    <MenuItem key={v.id} value={v.slug}>
                      {v.name} ({v.slug})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider sx={{ my: 2, borderColor: '#27272a' }} />

            {visibleRules.length > 0 && (
              <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 1.5 }}>
                <Checkbox
                  size="small"
                  checked={allVisibleChecked}
                  indeterminate={someVisibleChecked}
                  onChange={(e) => handleSelectAllVisible(e.target.checked)}
                  sx={{
                    mt: 0.25,
                    p: 0.25,
                    color: '#a1a1aa',
                    '&.Mui-checked': { color: '#818cf8' },
                    '&.MuiCheckbox-indeterminate': { color: '#818cf8' },
                  }}
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#e4e4e7', fontWeight: 600 }}>
                    Rules ({visibleRules.length})
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#71717a', display: 'block' }}>
                    All in list
                  </Typography>
                </Box>
              </Stack>
            )}

            {visibleRules.length === 0 && (
              <Typography variant="subtitle2" sx={{ color: '#e4e4e7', fontWeight: 600, mb: 1 }}>
                Rules (0)
              </Typography>
            )}

            <List dense disablePadding sx={{ pb: 4 }}>
              {visibleRules.map((r) => {
                const dom = domainById.get(r.domain_id) ?? r.domain_id;
                const crit = r.draft_criterion_id ? criterionById.get(r.draft_criterion_id) ?? '—' : '—';
                const ven = (r.publish_venues ?? []).filter(Boolean).join(', ') || '—';
                const checked = selectedRuleIdSet.has(r.id);
                return (
                  <ListItem
                    key={r.id}
                    disablePadding
                    sx={{
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor: checked ? 'rgba(129, 140, 248, 0.08)' : 'transparent',
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ width: '100%', py: 0.25, pr: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleRule(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          mt: 0.25,
                          p: 0.25,
                          color: '#a1a1aa',
                          '&.Mui-checked': { color: '#818cf8' },
                        }}
                      />
                      <ListItemText
                        primary={
                          <Typography component="span" variant="body2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                            {dom}
                          </Typography>
                        }
                        secondary={
                          <Stack component="span" spacing={0.25} sx={{ mt: 0.5 }}>
                            <Typography component="span" variant="caption" sx={{ color: '#a1a1aa', display: 'block' }}>
                              {crit} · {actionShort(r.action)}
                              {!r.enabled ? ' · off' : ''}
                            </Typography>
                            <Typography component="span" variant="caption" sx={{ color: '#71717a', display: 'block', wordBreak: 'break-word' }}>
                              {ven}
                            </Typography>
                          </Stack>
                        }
                        onClick={() => toggleRule(r.id)}
                        sx={{ cursor: 'pointer', m: 0 }}
                      />
                    </Stack>
                  </ListItem>
                );
              })}
              {visibleRules.length === 0 && (
                <Typography variant="body2" sx={{ color: '#71717a', py: 1 }}>
                  No rules match the current OR filters.
                </Typography>
              )}
            </List>
          </Box>
        </Box>
      </Drawer>

      <Box sx={{ px: { xs: 0, sm: 0.5 } }}>
        <Slider
          value={milestoneIdx}
          onChange={(_, v) => setMilestoneIdx(v as number)}
          step={1}
          marks={sliderMarks}
          min={0}
          max={MILESTONE_DEFS.length - 1}
          sx={{
            color: '#818cf8',
            '& .MuiSlider-markLabel': { color: '#a1a1aa', fontSize: 11 },
          }}
        />
      </Box>

      {loading && (
        <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
          Loading dashboard…
        </Typography>
      )}

      {!loading && summary && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <Box sx={{ p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#18181b', minWidth: 160 }}>
              <Typography variant="caption" sx={{ color: '#71717a' }}>
                Total runs
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fafafa' }}>
                {summary.total}
              </Typography>
            </Box>
            <Box sx={{ p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#18181b', minWidth: 160 }}>
              <Typography variant="caption" sx={{ color: '#71717a' }}>
                Avg runs / day
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fafafa' }}>
                {summary.avg_per_day}
              </Typography>
            </Box>
            <Box sx={{ p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#18181b', minWidth: 200 }}>
              <Typography variant="caption" sx={{ color: '#71717a' }}>
                Human hours saved
              </Typography>
              <Typography variant="caption" sx={{ color: '#52525b', display: 'block' }}>
                1 word ≈ 1 minute editorial time
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#34d399' }}>
                {summary.human_hours_saved} h
              </Typography>
              <Typography variant="caption" sx={{ color: '#71717a' }}>
                {summary.total_words} words
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
            <Box sx={{ flex: 1, p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#141414', minHeight: 160 }}>
              <Typography sx={{ fontWeight: 600, color: '#e4e4e7', mb: 1 }}>By domain</Typography>
              <Stack spacing={0.75}>
                {(summary.by_domain ?? []).slice(0, 12).map((row) => (
                  <Stack key={row.id || 'none'} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#d4d4d8', wordBreak: 'break-word' }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#818cf8', fontWeight: 600 }}>
                      {row.count}
                    </Typography>
                  </Stack>
                ))}
                {summary.by_domain?.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#71717a' }}>
                    No data in this range.
                  </Typography>
                )}
              </Stack>
            </Box>
            <Box sx={{ flex: 1, p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#141414', minHeight: 160 }}>
              <Typography sx={{ fontWeight: 600, color: '#e4e4e7', mb: 1 }}>By draft criterion</Typography>
              <Stack spacing={0.75}>
                {(summary.by_criterion ?? []).slice(0, 12).map((row) => (
                  <Stack key={row.id || 'none'} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#d4d4d8', wordBreak: 'break-word' }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#818cf8', fontWeight: 600 }}>
                      {row.count}
                    </Typography>
                  </Stack>
                ))}
                {summary.by_criterion?.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#71717a' }}>
                    No data in this range.
                  </Typography>
                )}
              </Stack>
            </Box>
            <Box sx={{ flex: 1, p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#141414', minHeight: 160 }}>
              <Typography sx={{ fontWeight: 600, color: '#e4e4e7', mb: 1 }}>By publish venue</Typography>
              <Stack spacing={0.75}>
                {(summary.by_venue ?? []).slice(0, 12).map((row) => (
                  <Stack key={row.id || 'none'} direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#d4d4d8', wordBreak: 'break-word' }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#818cf8', fontWeight: 600 }}>
                      {row.count}
                    </Typography>
                  </Stack>
                ))}
                {summary.by_venue?.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#71717a' }}>
                    No data in this range.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <Box sx={{ flex: 1, p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#141414' }}>
              <Typography sx={{ fontWeight: 600, color: '#e4e4e7', mb: 1 }}>Runs by weekday (UTC)</Typography>
              {weekdayChart && summary.total > 0 ? (
                <Bar data={weekdayChart} options={{ ...chartOpts, plugins: { ...chartOpts.plugins, legend: { display: false } } }} />
              ) : (
                <Typography variant="body2" sx={{ color: '#71717a' }}>
                  No distribution yet.
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, p: 2, border: '1px solid #27272a', borderRadius: 1, bgcolor: '#141414' }}>
              <Typography sx={{ fontWeight: 600, color: '#e4e4e7', mb: 1 }}>Runs by hour of day (UTC)</Typography>
              {hourChart && summary.total > 0 ? (
                <Bar
                  data={hourChart}
                  options={{
                    ...chartOpts,
                    plugins: { ...chartOpts.plugins, legend: { display: false } },
                  }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: '#71717a' }}>
                  No distribution yet.
                </Typography>
              )}
            </Box>
          </Stack>
        </>
      )}
    </Stack>
  );
};
